import { Alert, Box, Button, Chip, LinearProgress, Stack, Step, StepLabel, Stepper, Typography, useMediaQuery, useTheme } from '@mui/material';
import ReplayIcon from '@mui/icons-material/Replay';
import { useMutation } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import type { Job, JobLogLine } from '@api/api';
import { useJobStream } from '../api/useJobStream';
import { endpoints } from '../api/endpoints';
import { ErrorState, KeyValue, OneTimeSecret, RelTime, SkeletonRows } from './common';
import { VirtualLog } from './VirtualLog';
import { JOB_CHIP } from '../lib/status';
import { fmtTime } from '../lib/format';
import { useNotify } from './Notify';
import { useIsOwner } from '../auth/AuthProvider';

export const JOB_LABEL: Record<string, string> = {
  provision: 'Provision',
  deploy: 'Deploy',
  restart: 'Restart',
  stop: 'Stop',
  start: 'Start',
  backup: 'Backup',
  env: 'Apply keys',
  'reset-admin': 'Reset admin password',
  delete: 'Delete',
  'release-fetch': 'Fetch release',
};

/** Job types are `clone.<action>` on the server (`release-fetch` stays as is). */
export const jobKind = (type: string) => type.replace(/^clone\./, '');
export const jobLabel = (type: string) => JOB_LABEL[jobKind(type)] ?? type;

export function jobDuration(j: Job, now = Date.now()): string {
  if (!j.startedAt) return '—';
  const end = j.finishedAt ? new Date(j.finishedAt).getTime() : now;
  const s = Math.max(0, Math.round((end - new Date(j.startedAt).getTime()) / 1000));
  return s < 60 ? `${s} s` : `${Math.floor(s / 60)} min ${s % 60} s`;
}

/** The one-time admin password a provision / reset-admin job returns (contract: result.adminPassword). */
export function jobSecret(j: Job): { login: string | null; password: string } | null {
  const r = j.result ?? {};
  const pw = (r.adminPassword ?? r.password ?? r.superAdminPassword) as string | undefined;
  if (!pw) return null;
  return { login: (r.adminLogin ?? r.login ?? null) as string | null, password: pw };
}

const streamColor: Record<JobLogLine['stream'], string> = { cmd: 'primary.main', out: 'text.primary', err: 'error.main', info: 'text.secondary' };

export function JobView({ id, logHeight = 420 }: { id: number; logHeight?: number | string }) {
  const { job, lines, live } = useJobStream(id);
  const theme = useTheme();
  const wide = useMediaQuery(theme.breakpoints.up('md'));
  const notify = useNotify();
  const navigate = useNavigate();
  const owner = useIsOwner();
  const retry = useMutation({
    mutationFn: () => endpoints.retryJob(id),
    onSuccess: (j) => {
      notify.ok(`Job #${j.id} started`);
      navigate(`/jobs/${j.id}`);
    },
    onError: (e) => notify.error(e),
  });

  if (job.isLoading) return <SkeletonRows rows={4} />;
  if (job.isError || !job.data) return <ErrorState error={job.error} onRetry={() => void job.refetch()} />;
  const j = job.data;
  const done = j.steps.filter((s) => s.status === 'done' || s.status === 'skipped').length;
  const pct = j.steps.length ? (done / j.steps.length) * 100 : 0;
  const activeIdx = j.steps.findIndex((s) => s.status === 'running');
  const secret = jobSecret(j);
  const otherResult = Object.entries(j.result ?? {}).filter(([k]) => !/password/i.test(k) && k !== 'adminLogin');

  return (
    <Stack spacing={2}>
      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
        <Typography variant="h6" component="h2">
          {jobLabel(j.type)} <Typography component="span" color="text.secondary">#{j.id}</Typography>
        </Typography>
        <Chip size="small" color={JOB_CHIP[j.status]} label={j.status} />
        {live && <Chip size="small" variant="outlined" color="info" label="live" />}
        <Box sx={{ flex: 1 }} />
        <Typography variant="body2" color="text.secondary">
          {j.createdBy ?? 'system'} · started <RelTime iso={j.startedAt ?? j.createdAt} /> · {jobDuration(j)}
        </Typography>
        {j.status === 'failed' && owner && (
          <Button size="small" startIcon={<ReplayIcon />} onClick={() => retry.mutate()} disabled={retry.isPending}>
            Retry
          </Button>
        )}
      </Stack>

      <Box>
        <LinearProgress variant={j.status === 'queued' ? 'indeterminate' : 'determinate'} value={pct} color={j.status === 'failed' ? 'error' : j.status === 'succeeded' ? 'success' : 'primary'} sx={{ height: 6, borderRadius: 3, mb: 2 }} aria-label="Job progress" />
        <Box sx={{ overflowX: 'auto' }}>
          <Stepper activeStep={activeIdx === -1 ? done : activeIdx} orientation={wide ? 'horizontal' : 'vertical'} alternativeLabel={wide && j.steps.length > 3}>
            {j.steps.map((s) => (
              <Step key={s.name} completed={s.status === 'done'}>
                <StepLabel error={s.status === 'failed'} optional={s.status === 'running' ? <Typography variant="caption" color="primary">running…</Typography> : s.status === 'skipped' ? <Typography variant="caption">skipped</Typography> : undefined}>
                  {s.name}
                </StepLabel>
              </Step>
            ))}
          </Stepper>
        </Box>
      </Box>

      {j.error && (
        <Alert severity="error">
          <strong>Failed.</strong> {j.error}
        </Alert>
      )}
      {secret && j.status === 'succeeded' && (
        <OneTimeSecret title="Super admin password" value={secret.password}>
          {secret.login && (
            <Typography variant="body2">
              Login: <strong>{secret.login}</strong>
            </Typography>
          )}
        </OneTimeSecret>
      )}
      {otherResult.length > 0 && <KeyValue items={otherResult.map(([k, v]) => [k, typeof v === 'string' && /^https?:/.test(v) ? <a href={v} target="_blank" rel="noreferrer">{v}</a> : String(v)])} />}

      <VirtualLog
        items={lines}
        follow={live || j.status === 'running'}
        height={logHeight}
        getKey={(l) => l.seq}
        empty={<Box sx={{ p: 2, color: 'text.secondary' }}>{j.status === 'queued' ? 'Waiting in the queue…' : 'No log lines.'}</Box>}
        render={(l) => (
          <Box sx={{ display: 'flex', gap: 1.5, px: 1.5, color: streamColor[l.stream], fontWeight: l.stream === 'info' ? 600 : 400, '&:hover': { bgcolor: 'action.hover' } }}>
            <Box component="span" sx={{ color: 'text.disabled', flexShrink: 0 }}>
              {fmtTime(l.ts)}
            </Box>
            <Box component="span" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
              {l.line}
            </Box>
          </Box>
        )}
      />
    </Stack>
  );
}
